/**
 * SettingsBar Component for Poker Reborn
 *
 * Simple top navigation bar for the Settings screen with:
 * - Title ("Settings")
 * - Close button (returns to previous screen)
 *
 * @see docs/screens/settings/SETTINGS_HIERARCHY.md - Component hierarchy
 */

import { Container, Graphics, Text } from 'pixi.js';
import { Button, DANGER_BUTTON_STYLE } from '@/components/common/Button';

// =============================================================================
// STYLES
// =============================================================================

const STYLES = {
  /** Bar dimensions */
  bar: {
    height: 48,
    heightMobile: 44,
    backgroundColor: 0x000000,
    backgroundAlpha: 0.6,
    borderColor: 0xffffff,
    borderAlpha: 0.1,
    borderWidth: 1,
    padding: 12,
  },

  /** Title */
  title: {
    fontSize: 18,
    fontFamily: 'Roboto, Arial, sans-serif',
    textColor: 0xffffff,
  },

  /** Button dimensions */
  button: {
    width: 80,
    widthMobile: 36,
    height: 36,
    fontSize: 14,
  },

  /** Responsive breakpoints */
  breakpoints: {
    mobile: 480,
  },
};

// =============================================================================
// SETTINGSBAR COMPONENT
// =============================================================================

export class SettingsBar extends Container {
  private screenWidth: number;
  private screenHeight: number;

  // Components
  private background: Graphics;
  private titleText: Text;
  private closeButton: Button;

  // Callbacks
  public onClose?: () => void;

  constructor(width: number, height: number) {
    super();
    this.screenWidth = width;
    this.screenHeight = height;

    // Create background
    this.background = new Graphics();
    this.addChild(this.background);

    // Create title text
    this.titleText = new Text({
      text: 'Settings',
      style: {
        fill: STYLES.title.textColor,
        fontSize: STYLES.title.fontSize,
        fontFamily: STYLES.title.fontFamily,
        fontWeight: 'bold',
      },
    });
    this.titleText.anchor.set(0, 0.5); // Left-aligned, vertically centered
    this.addChild(this.titleText);

    // Create close button
    this.closeButton = new Button({
      label: 'Close',
      width: STYLES.button.width,
      height: STYLES.button.height,
      style: {
        ...DANGER_BUTTON_STYLE,
        fontSize: STYLES.button.fontSize,
      },
    });
    this.closeButton.onClick = () => this.onClose?.();
    this.addChild(this.closeButton);

    // Initial layout
    this.layout();
  }

  /**
   * Resize the settings bar
   */
  resize(width: number, height: number): void {
    this.screenWidth = width;
    this.screenHeight = height;
    this.layout();
  }

  /**
   * Get the bar height for layout calculations
   */
  getBarHeight(): number {
    const isMobile = this.screenWidth < STYLES.breakpoints.mobile;
    return isMobile ? STYLES.bar.heightMobile : STYLES.bar.height;
  }

  // ===========================================================================
  // PRIVATE METHODS
  // ===========================================================================

  private layout(): void {
    const isMobile = this.screenWidth < STYLES.breakpoints.mobile;
    const barHeight = isMobile ? STYLES.bar.heightMobile : STYLES.bar.height;

    // Draw background
    this.background.clear();
    this.background.rect(0, 0, this.screenWidth, barHeight);
    this.background.fill({ color: STYLES.bar.backgroundColor, alpha: STYLES.bar.backgroundAlpha });

    // Draw bottom border
    this.background.rect(0, barHeight - STYLES.bar.borderWidth, this.screenWidth, STYLES.bar.borderWidth);
    this.background.fill({ color: STYLES.bar.borderColor, alpha: STYLES.bar.borderAlpha });

    const buttonY = (barHeight - STYLES.button.height) / 2;

    // Update button size for mobile/desktop
    if (isMobile) {
      this.closeButton.label = 'X';
      this.closeButton.resize(STYLES.button.widthMobile, STYLES.button.height);
    } else {
      this.closeButton.label = 'Close';
      this.closeButton.resize(STYLES.button.width, STYLES.button.height);
    }

    // Position title (left side)
    this.titleText.x = STYLES.bar.padding;
    this.titleText.y = barHeight / 2;

    // Position close button (right side)
    const closeButtonWidth = isMobile ? STYLES.button.widthMobile : STYLES.button.width;
    this.closeButton.x = this.screenWidth - closeButtonWidth - STYLES.bar.padding;
    this.closeButton.y = buttonY;
  }
}
